/**
 * Localised catalogues for the profile's dropdowns — currencies, timezones and
 * languages — built from the runtime's own `Intl` data.
 *
 * WHY NOT A PACKAGE: every ISO 4217 currency, IANA timezone and BCP-47 language
 * is already in the browser and Node, localised per language by `Intl.DisplayNames`.
 * A static list would add a dependency to carry fewer codes, stale on the day a
 * country redenominates. `Intl.supportedValuesOf` lists what the runtime actually
 * knows; the fallbacks below only exist for runtimes without it.
 *
 * Values stored are the canonical codes (XAF, Africa/Douala, en) — what the
 * backend persists — while labels are what the administrator reads.
 */

export interface LocalisedOption {
  value: string;
  label: string;
  description?: string;
}

/** The currencies a Central African school is most likely to need, in order. */
const FALLBACK_CURRENCIES = [
  'XAF', 'XOF', 'NGN', 'ZAR', 'KES', 'GHS', 'EGP', 'MAD', 'TZS', 'UGX',
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR', 'CHF', 'NZD',
  'AED', 'SAR', 'PKR', 'BRL', 'MXN', 'RWF', 'BIF', 'CDF', 'GNF', 'SLL',
];

const FALLBACK_TIMEZONES = [
  'UTC',
  'Africa/Abidjan', 'Africa/Accra', 'Africa/Algiers', 'Africa/Cairo',
  'Africa/Douala', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Johannesburg',
  'Europe/London', 'Europe/Paris', 'America/New_York', 'Asia/Dubai',
  'Asia/Singapore', 'Asia/Tokyo', 'Asia/Kolkata',
];

const FALLBACK_LANGUAGES = [
  'en', 'fr', 'ar', 'zh', 'de', 'es', 'pt', 'it', 'sw', 'yo', 'ig', 'am', 'ha',
];

function supportedValues(kind: 'currency' | 'timeZone' | 'unit' | 'calendar' | 'collation' | 'numberingSystem'): string[] {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf(kind);
    }
  } catch {
    // A runtime with the function but no data for this kind.
  }
  return [];
}

/** The local display name of a currency code, e.g. "Central African CFA Franc". */
export function currencyName(code: string, locale = 'en'): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    if (name) return name;
  } catch {
    // Fall through to the code itself.
  }
  return code;
}

/** The symbol a currency renders with, e.g. "FCFA" for XAF. Falls back to the code. */
export function currencySymbol(code: string, locale = 'en'): string {
  try {
    const symbol = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;
    if (symbol && symbol !== code) return symbol;
  } catch {
    // Fall through.
  }
  return code;
}

/** Every currency the runtime knows, by localised name, searchable by code too. */
export function currencyOptions(locale = 'en'): LocalisedOption[] {
  const codes = supportedValues('currency');
  const list = codes.length ? codes : FALLBACK_CURRENCIES;
  return [...new Set(list)]
    .map((code) => ({
      value: code,
      label: currencyName(code, locale),
      description: `${code} · ${currencySymbol(code, locale)}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

/** The offset a timezone renders at, e.g. "GMT+1". Empty when unknown. */
export function timezoneOffset(tz: string, locale = 'en'): string {
  try {
    const offset = new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
      .formatToParts()
      .find((part) => part.type === 'timeZoneName')?.value;
    return offset || '';
  } catch {
    return '';
  }
}

/** Every IANA timezone, sorted by region then city, each with its current offset. */
export function timezoneOptions(locale = 'en'): LocalisedOption[] {
  const zones = supportedValues('timeZone');
  const list = zones.length ? zones : FALLBACK_TIMEZONES;
  return [...new Set(list)]
    .sort((a, b) => a.localeCompare(b, locale))
    .map((tz) => ({ value: tz, label: tz, description: timezoneOffset(tz, locale) }));
}

/** The local display name of a language code, e.g. "English" for en. */
export function languageName(code: string, locale = 'en'): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
    if (name) return name;
  } catch {
    // Fall through.
  }
  return code;
}

/** Languages by localised name, with English and French pinned where available. */
export function languageOptions(locale = 'en'): LocalisedOption[] {
  const all = FALLBACK_LANGUAGES;
  const pinned = ['en', 'fr'].filter((code) => all.includes(code));
  const rest = all
    .filter((code) => !pinned.includes(code))
    .sort((a, b) => languageName(a, locale).localeCompare(languageName(b, locale), locale));
  return [...pinned, ...rest]
    .map((code) => ({ value: code, label: languageName(code, locale), description: code }))
    .filter((option) => option.label.trim().length > 0);
}